import { BASE_API_URL } from '../const';

enum RequestMethodEnum {
	get = 'GET',
	post = 'POST',
	put = 'PUT',
	delete = 'Delete',
}

type RequestType = {
	url: string;
	init?: RequestInit;
};

export type ErrorType = {
	statusCode: number | null;
	message: string | null;
	name: string | null;
	error: boolean;
	errors?: { _id: string; missing?: boolean; price?: number }[];
};

type ResponseType<T> = {
	result: T | null;
	error: ErrorType | null;
};

const getPublicAPIPath = (url: string = ''): string => {
	const clearURL = url[0] === '/' ? url.slice(1) : url;
	if (url.includes('https://')) return url;
	return `${BASE_API_URL}/${clearURL}`;
};

const _getRequestOptions = (init?: RequestInit): RequestInit => {
	const { headers, ...restInitOptions } = init || {};

	return {
		headers: {
			'Content-Type': 'application/json',
			Accept: '*/*',
			'Accept-Language': 'en',
			...headers,
		},
		...restInitOptions,
	};
};

const _request = async <T>({ url, init }: RequestType): Promise<ResponseType<T>> => {
	const _url = getPublicAPIPath(url);
	const options = _getRequestOptions(init);
	try {
		const rs = await fetch(_url, options);
		const parsedResponse = (await rs.json()) as T | ErrorType;

		if ((parsedResponse as ErrorType).error) {
			const error = parsedResponse as ErrorType;

			return {
				result: null,
				error: {
					error: true,
					name: error.name ?? null,
					statusCode: error.statusCode ?? null,
					message: error.message ?? null,
					errors: error.errors,
				},
			};
		}

		return {
			result: parsedResponse as T,
			error: null,
		};
	} catch (e) {
		const error: any = e;

		return {
			result: null,
			error: {
				statusCode: error?.statusCode ?? null,
				message: error?.message ?? null,
				name: error?.name ?? null,
				error: true,
			},
		};
	}
};

const request = {
	get: <T>(url: string, config?: RequestInit) =>
		_request<T>({
			url,
			init: { method: RequestMethodEnum.get, ...config },
		}),
	post: <T>(url: string, body: any = {}, config?: RequestInit) =>
		_request<T>({
			url,
			init: {
				method: RequestMethodEnum.post,
				body: JSON.stringify(body),
				...config,
			},
		}),
	put: <T>(url: string, body: any = {}, config?: RequestInit) =>
		_request<T>({
			url,
			init: {
				method: RequestMethodEnum.put,
				body: JSON.stringify(body),
				...config,
			},
		}),
	delete: <T>(url: string, config?: RequestInit) =>
		_request<T>({
			url,
			init: { method: RequestMethodEnum.delete, ...config },
		}),
};

export default request;
